import { BASE_URL } from "@/Store/Action";
import { Button } from "flowbite-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useQueryClient } from "@tanstack/react-query";

function Address({
  onPrev,
  onNext,
  guideline,
}: {
  onPrev: () => void;
  onNext: () => void;
  guideline: any;
}) {
  const queryClient = useQueryClient();
  const { secretkey } = useParams<{ secretkey: string }>();
  const [zipCode, setZipCode] = useState("");
  const [address, setAddress] = useState("");
  const [detailAddress, setDetailAddress] = useState("");
  const [receiverName, setReceiverName] = useState(guideline?.name || "");
  const [receiverPhone, setReceiverPhone] = useState(guideline?.phone || "");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const submitAddress = () => {
    if (!zipCode || !address || !receiverName || !receiverPhone) {
      Swal.fire({
        title: "모든 필드를 입력해주세요",
        icon: "warning",
        confirmButtonText: "확인했어요",
        customClass: {
          confirmButton:
            "bg-yellow-500 text-white px-6 py-2 rounded-full text-sm hover:bg-yellow-600",
          popup: "rounded-lg shadow text-center",
        },
      });

      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);

    const payload = {
      is_package: true,
      secret_key: secretkey,
      user_shipping_info: {
        address: address,
        address_detail: detailAddress,
        receiver_name: receiverName,
        receiver_phone: receiverPhone,
        zip_code: zipCode,
      },
    };

    try {
      const res = await fetch(`${BASE_URL}/wg/attended`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("서버 오류");

      Swal.fire({
        title: "주소가 성공적으로 등록되었습니다",
        icon: "success",
        confirmButtonText: "확인",
        customClass: {
          confirmButton:
            "bg-[#0F1E6C] text-white px-6 py-2 rounded-full text-sm hover:bg-[#1A2B80]",
          popup: "rounded-lg shadow text-center",
        },
      });

      queryClient.refetchQueries({queryKey: ["campaign-wg"]})
    } catch (err) {
      Swal.fire({
        title: "에러 발생",
        text: `${err}`,
        icon: "error",
        confirmButtonText: "닫기",
        customClass: {
          confirmButton:
            "bg-red-600 text-white px-6 py-2 rounded-full text-sm hover:bg-red-700",
          popup: "rounded-lg shadow text-center",
        },
      });
    }
  };

  const openPostcode = () => {
    const element_layer = document.getElementById("postcode-container");
    if (!element_layer) return;

    new window.daum.Postcode({
      oncomplete: function (data: any) {
        const fullAddress = data.address;
        const zonecode = data.zonecode;

        setZipCode(zonecode);
        setAddress(fullAddress);

        // Clear iframe content setelah pemilihan alamat
        element_layer.innerHTML = "";
        element_layer.style.display = "none";
      },
      onresize: function (size: { width: number; height: number }) {
        element_layer.style.height = size.height + "px";
      },
      width: "100%",
      height: "100%",
    }).embed(element_layer);

    // Munculkan iframe container
    element_layer.style.display = "block";
  };

  return (
    <>
      <div className="">
        {/* Title */}
        <div className="border pb-2 flex flex-col gap-1 ps-3 bg-gray-100 rounded-lg pt-5">
          <h2 className="text-sm font-bold text-gray-800 mb-1">배송지 입력</h2>
          <p className="text-xs text-red-500 mb-4">
            배송을 위해 수령인, 연락처, 상세주소를 정확히 입력해 주세요.
          </p>
        </div>
        {!guideline.user_shipping_info.address && (
          <div className="">
            {/* Zip Code */}
            <div className="flex flex-col items-center justify-center">
              <div className="px-4 py-1 mt-3 flex gap-2 w-full max-w-xl">
                <div className="w-1/2">
                  <h3 className="font-bold mb-2 text-sm">우편번호</h3>
                  <input
                    readOnly
                    type="text"
                    value={zipCode}
                    className="w-full border border-gray-300 text-xs rounded-md py-3"
                    placeholder="우편번호를 검색하세요"
                  />
                </div>
                <div className="w-1/2">
                  <h3 className="font-bold mb-2 text-sm text-white">
                    우편번호
                  </h3>
                  <Button
                    className="bg-[#0F1E6C] w-full rounded-md"
                    onClick={openPostcode}
                  >
                    우편번호 찾기
                  </Button>
                </div>
              </div>

              {/* Embed Iframe Container */}
              <div
                id="postcode-container"
                className="hidden relative w-[95%] max-w-xl h-[400px] mt-4 mx-auto border border-gray-300 rounded-lg"
              ></div>
            </div>

            {/* Address */}
            <div className="px-4 py-1 ">
              <h3 className="font-bold mb-2 text-sm">기본주소</h3>
              <input
                type="text"
                value={address}
                className="w-full border border-gray-300 text-xs rounded-md py-3 "
                placeholder="기본주소가 자동으로 입력됩니다"
              />
            </div>
            {/* Detail Address */}
            <div className="px-4 py-1 ">
              <h3 className="font-bold mb-2 text-sm">상세주소</h3>
              <input
                type="text"
                value={detailAddress}
                onChange={(e) => setDetailAddress(e.target.value)}
                className="w-full border border-gray-300 text-xs rounded-md py-3 "
                placeholder="예: 아파트 101동 202호"
              />
            </div>
            {/* Name */}
            <div className="px-4 py-1 ">
              <h3 className="font-bold mb-2 text-sm">수령인 이름</h3>
              <input
                type="text"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                className="w-full border border-gray-300 text-xs rounded-md py-3 "
                placeholder="예: 김지민"
              />
            </div>

            {/* Phone */}
            <div className="px-4 py-1 ">
              <h3 className="font-bold mb-2 text-sm">연락처</h3>
              <input
                type="text"
                value={receiverPhone}
                onChange={(e) => setReceiverPhone(e.target.value)}
                className="w-full border border-gray-300 text-xs rounded-md py-3 "
                placeholder="예: 010-1234-5678"
              />
            </div>

            {/* Button Navigation */}
            <div className="max-w-xl mx-auto fixed z-20 bottom-0 left-0 right-0 bg-white border-t justify-between px-4 py-3 flex gap-2">
              <div
                onClick={onPrev}
                className="flex pr-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center cursor-pointer"
              >
                <ChevronLeft />
                <div className="font-semibold">이전</div>
              </div>
              <Button
                onClick={submitAddress}
                className="rounded-full w-3/4 bg-[#0F1E6C] text-white text-base h-11"
              >
                등록
              </Button>
            </div>
          </div>
        )}

        {guideline.user_shipping_info.address && (
          <div className="  text-sm mt-5 px-2">
            <p className="font-bold">배송주소</p>
            <div className="bg-[#F4F5FF] rounded-md p-5 mt-2 ">
              <div className="text-gray-700 space-y-2 text-xs">
                <div className="flex ">
                  <div className="w-1/4 text-gray-400">우편번호</div>
                  <div className="w-3/4">{guideline.user_shipping_info.zip_code}</div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400">기본주소</div>
                  <div className="w-3/4">
                    {guideline.user_shipping_info.address}{" "}
                    {guideline.user_shipping_info.address_detail ? (
                      <>({guideline.user_shipping_info.address_detail})</>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400">수령인 이름</div>
                  <div className="w-3/4">{guideline.user_shipping_info.receiver_name}</div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400">연락처</div>
                  <div className="w-3/4">{guideline.user_shipping_info.receiver_phone}</div>
                </div>
              </div>
            </div>
            {/* Button Navigation */}
            <div className="max-w-xl mx-auto fixed z-20 bottom-0 left-0 right-0 bg-white border-t justify-between px-4 py-3 flex gap-2">
              <div
                onClick={onPrev}
                className="flex pr-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center"
              >
                <ChevronLeft />
                <div className="font-semibold">이전</div>
              </div>

              <div
                onClick={() => onNext()}
                className="flex ps-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center"
              >
                <div className="font-semibold"> 다음</div>
                <ChevronRight className=""></ChevronRight>
              </div>
            </div>
          </div>
        )}
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-30 px-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md px-6 py-8 text-sm">
            <div className="border-b mb-4">
              <h2 className="text-lg font-semibold text-center mb-4">
                [ 주소를 확인해주세요 ]
              </h2>
            </div>

            <div className="text-gray-700 space-y-2 text-xs">
              <div className="flex ">
                <div className="w-1/4">우편번호</div>
                <div className="w-3/4">{zipCode}</div>
              </div>
              <div className="flex ">
                <div className="w-1/4">기본주소</div>
                <div className="w-3/4">
                  {address} {detailAddress ? <>({detailAddress})</> : ""}
                </div>
              </div>
              <div className="flex ">
                <div className="w-1/4">수령인 이름</div>
                <div className="w-3/4">{receiverName}</div>
              </div>
              <div className="flex ">
                <div className="w-1/4">연락처</div>
                <div className="w-3/4">{receiverPhone}</div>
              </div>
            </div>

            <div className="flex justify-between gap-4 mt-6">
              <button
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-full hover:bg-gray-300"
                onClick={() => setShowConfirmModal(false)}
              >
                취소
              </button>
              <button
                className="flex-1 bg-[#0F1E6C] text-white py-2 rounded-full hover:bg-[#1A2B80]"
                onClick={handleConfirmSubmit}
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Address;
